import { Action } from "@ngrx/store";

import { ModelState } from "./state";
import * as Actions from "./actions";
import { assert } from "../../utils/debug";

const PATH = "@path";

export function modelReducer(
    state: ModelState = new ModelState(),
    action: Actions.Actions,
): ModelState {
    if (action.type === Actions.ReplaceDatabaseAction.type) {
        const x: Actions.ReplaceDatabaseAction = action;
        return Object.assign({}, state, {
            ConsultationProcess: decorate(action.cp, ["ConsultationProcess"]),
        });
    } else if (action.type === Actions.AddAction.type) {
        if (action.entity === undefined || action.entity === null) {
            return state;
        }
        return add(state, [...action.collection[PATH], null], action.entity);
    } else if (action.type === Actions.DeleteAction.type) {
        if (
            action.entity === undefined ||
            action.entity === null ||
            !action.entity.hasOwnProperty(PATH)
        ) {
            return state;
        }
        return remove(state, action.entity[PATH], action.entity);
    } else if (action.type === Actions.UpdateAction.type) {
        if (
            action.entity === undefined ||
            action.entity === null ||
            !action.entity.hasOwnProperty(PATH)
        ) {
            return state;
        }
        return update(state, action.entity[PATH], action.entity);
    } else if (action.type === Actions.MergeAction.type) {
        if (action.path == null || action.diff == null) {
            return state;
        }
        return merge(state, action.path, action.diff);
    } else {
        return state;
    }
}

function add<T, E>(branch: T, path: string[], entity: E): T {
    return updateAt(
        branch,
        path,
        (object) => {
            if (object !== undefined) {
                assert(false, `Would replace an existing object by adding.`);
                return object;
            }
            return entity;
        },
        (array) => {
            assert(false, `Would replace an existing array by adding.`);
            return array;
        },
    );
}

function update<T>(branch: T, path: string[], value: any): T {
    return updateAt(
        branch,
        path,
        (object) => {
            return value;
        },
        (array) => {
            assert(false, `Would replace an array.`);
            return array;
        },
    );
}

function merge<T>(branch: T, path: string[], diff: any): T {
    return updateAt(
        branch,
        path,
        (object) => {
            return Object.assign({}, object, diff);
        },
        (array) => {
            assert(false, `Would merge an array.`);
            return array;
        },
    );
}

function remove<T>(branch: T, path: string[], entity: any) {
    const collectionPath = path.slice(0, -1);
    const idx: number = parseInt(path.slice(-1)[0]);
    return updateAt(
        branch,
        collectionPath,
        (object) => {
            assert(
                false,
                `Would remove a property instead of an array element.`,
            );
            return object;
        },
        (array) => {
            return [...array.slice(0, idx), ...array.slice(idx + 1)];
        },
    );
}

function decorate<T>(inObject: T, path: string[]): T {
    if (inObject === undefined || inObject === null) {
        return inObject;
    }
    const fresh = Array.isArray(inObject) ? [] : {};
    const object = Object.assign(fresh, inObject);
    object[PATH] = path;
    for (const key in object) {
        if (key.startsWith("@") || !object.hasOwnProperty(key)) {
            continue;
        }
        const value: any = object[key];
        if (
            value === undefined ||
            value === null ||
            !(value instanceof Object)
        ) {
            continue;
        }
        object[key] = decorate(value, [...path, key]);
    }
    return object;
}

function updateAt<T>(
    branch: T,
    path: string[],
    updateObject: (object: any) => any,
    updateArray: (array: any[]) => any[],
): T {
    return updateAtInt<T>(path, branch, path, updateObject, updateArray);
}

function updateAtInt<T>(
    fullPath: string[],
    branch: T,
    path: string[],
    updateObject: (object: any) => any,
    updateArray: (array: any[]) => any[],
): T {
    if (path.length <= 0) {
        let newValue;
        if (!Array.isArray(branch)) {
            newValue = updateObject(branch);
        } else {
            newValue = updateArray(branch);
        }
        if (newValue != null) {
            newValue = decorate(newValue, fullPath);
        }
        //console.log(`Update: ${fullPath} <- ${newValue ? newValue.Id : newValue}`)
        return newValue;
    }
    if (!Array.isArray(branch)) {
        const [key, ...childPath] = path;
        const object: T = Object.assign({}, branch);
        object[key] = updateAtInt(
            fullPath,
            branch[key],
            childPath,
            updateObject,
            updateArray,
        );
        return object;
    } else {
        const [key, ...childPath] = path;
        let idx: number;
        if (key !== null) {
            idx = parseInt(key);
        } else {
            idx = branch.length;
            fullPath = [...fullPath.slice(0, -1), idx.toString()];
        }
        let array: any;
        const object = updateAtInt(
            fullPath,
            branch[idx],
            childPath,
            updateObject,
            updateArray,
        );
        if (object !== undefined) {
            array = [...branch.slice(0, idx), object, ...branch.slice(idx + 1)];
        } else {
            array = [...branch.slice(0, idx), ...branch.slice(idx + 1)];
        }
        array[PATH] = branch[PATH];
        return array as any as T;
    }
}
